import {
  Component,
  ComponentFactoryResolver,
  ViewChild,
  ViewContainerRef,
} from "@angular/core";
import { DomSanitizer, SafeResourceUrl } from "@angular/platform-browser";
import { TranslateService } from "@ngx-translate/core";
import { LOCATION_GROUP } from "app/access-control/common-permission-asks";
import { ActivityLog, Facilites, Teams } from "app/access-control/permissions";
import { PermissionsResponse } from "app/access-control/permissions-response";
import { Events, IonicPage, NavController, NavParams } from "ionic-angular";
import { FeatureToggleDataService } from "providers/features-service/feature-toggle.data.service";
import { ReportExecutionService } from "providers/report-execution-service/report-execution.service";
import { Observable, Subscription } from "rxjs";
import { of } from "rxjs/observable/of";
import { AccessControlService } from "../../../app/access-control/access-control-service";
import { AuthService } from "../../../app/auth/auth.service";
import { StateManagementService } from "../../../providers/state-management/state-management.service";
import { UsersService } from "../../../providers/users-service/users.service";
import { CompanyPage } from "../company";

@IonicPage({
  segment: "company/company-management/location-group",
})
@Component({
  selector: "location-group",
  templateUrl: "location-group.html",
})
export class LocationGroup extends CompanyPage {
  @ViewChild("componentPlaceholder", { read: ViewContainerRef })
  componentPlaceholderRef: ViewContainerRef;

  locationGroupName: string = "";

  tabs: string[] = [];
  activeTab: string;
  subscriptions: Subscription[] = [];
  locationGroupPermissionsAccess = {} as PermissionsResponse;
  hasTeamsAll: boolean = false;
  hasTeamsView: boolean = false;
  hasActivityLogAll: boolean = false;
  hasActivityLogView: boolean = false;
  hasFacilitesView: boolean = false;
  newBillingFacilityToggle: boolean = false;
  newBillingAllPayerToggle: boolean = false;
  public featureToggleData;

  qsDashboard: SafeResourceUrl = null;
  showReport = false;
  reportTitle = "";

  // The tab whose content is currently mounted in componentPlaceholderRef. Each
  // load*() is an async import().then(createComponent) while clear() is
  // synchronous, so re-dispatching the same tab stacks duplicate components (and
  // duplicates every HTTP call they make). setTabsAndActiveTabs is legitimately
  // reached more than once per load — from getAccess() and from
  // getLocationAccess() — so the dispatch has to be idempotent.
  private loadedTab: string = null;

  // Stable handler reference so ngOnDestroy can unsubscribe THIS handler only.
  // ionic-angular's Events.unsubscribe(topic) with no handler argument deletes
  // the whole topic, taking every other component's subscriber with it.
  private payerReportHandler = () => this.onRunPayerReport();
  private payerReportSubscribed = false;

  ionViewWillLoad() {
    this.checkLocationGroupAccess();
    this.getAccess();
  }

  ngOnInit() {
    // getUser() can still be null on a cold deep-link; an unguarded .tenantName
    // throws out of ngOnInit and leaves the page blank.
    const user = this.authSvc.getUser();
    this.locationGroupName = (user && user.tenantName) || "";
    // The active tab is chosen by setTabsAndActiveTabs() once permissions and
    // feature toggles are known. Picking one here would mount tab content before
    // the RBAC gate has been evaluated.
  }

  ionViewDidEnter() {
    this.stateManagementService.setSectionId("Location");
  }

  constructor(
    protected accessControlService: AccessControlService,
    protected navCtrl: NavController,
    protected navParams: NavParams,
    protected userService: UsersService,
    protected stateManagementService: StateManagementService,
    protected authService: AuthService,
    private resolver: ComponentFactoryResolver,
    protected authSvc: AuthService,
    public featureToggleDataService: FeatureToggleDataService,
    protected reportService: ReportExecutionService,
    private sanitizer: DomSanitizer,
    protected translate: TranslateService,
    protected events: Events
  ) {
    super(
      accessControlService,
      navCtrl,
      navParams,
      userService,
      stateManagementService,
      authSvc
    );
  }

  getAccess() {
    this.loadFeatureToggleData().subscribe((featureToggleResponseData) => {
      if (featureToggleResponseData) {
        this.featureToggleData = featureToggleResponseData;
        this.featureToggleData.forEach((element) => {
          if (element.name.toLowerCase() === "new billing facility") {
            this.newBillingFacilityToggle = element.active;
          }
          if (element.name.toLowerCase() === "new billing all payer") {
            this.newBillingAllPayerToggle = element.active;
          }
        });
      }
    });

    this.setTabsAndActiveTabs();
  }

  private loadFeatureToggleData(): Observable<object> {
    return of(this.featureToggleDataService.getAllStoredToggles());
  }

  checkLocationGroupAccess() {
    this.subscriptions.push(
      this.accessControlService
        .getPermission(LOCATION_GROUP)
        .subscribe((data) => {
          if (data) {
            this.locationGroupPermissionsAccess = data;
            this.getLocationAccess();
          }
        })
    );
  }

  getLocationAccess() {
    this.hasTeamsAll = this.accessControlService.hasAccess(
      this.locationGroupPermissionsAccess,
      Teams.TeamsAll
    );
    this.hasTeamsView = this.accessControlService.hasAccess(
      this.locationGroupPermissionsAccess,
      Teams.TeamsView
    );
    this.hasActivityLogAll = this.accessControlService.hasAccess(
      this.locationGroupPermissionsAccess,
      ActivityLog.ActivityLogAll
    );
    this.hasActivityLogView = this.accessControlService.hasAccess(
      this.locationGroupPermissionsAccess,
      ActivityLog.ActivityLogView
    );
    this.hasFacilitesView = this.accessControlService.hasAccess(
      this.locationGroupPermissionsAccess,
      Facilites.FacilitesView
    );
    this.setTabsAndActiveTabs();
  }

  setTabsAndActiveTabs() {
    this.tabs = [];
    if (this.hasTeamsAll || this.hasTeamsView) {
      this.tabs.push("Teams");
    }
    if (this.hasActivityLogAll || this.hasActivityLogView) {
      this.tabs.push("Activity Log");
    }
    if (this.hasFacilitesView && this.newBillingFacilityToggle) {
      this.tabs.push("Facilities");
    }
    if (this.hasFacilitesView && this.newBillingAllPayerToggle) {
      this.tabs.push("Payers");
    }
    if (this.tabs.length > 0) {
      // A requested tab (navParams / deep link) is honored only if the user is
      // actually entitled to it — otherwise it is a way around the gate above.
      const requested: string = this.navParams.get("activeTab");
      this.setActiveTab(
        this.tabs.indexOf(requested) !== -1 ? requested : this.tabs[0]
      );
    } else {
      // This user is not entitled to view any location information. Make sure
      // nothing is left mounted from an earlier, more permissive evaluation.
      this.activeTab = undefined;
      this.loadedTab = null;
      if (this.componentPlaceholderRef) {
        this.componentPlaceholderRef.clear();
      }
    }
  }

  setActiveTab(tab: string) {
    if (tab === this.activeTab && this.loadedTab === tab) {
      return;
    }
    this.activeTab = tab;
    this.loadedTab = tab;
    if (this.componentPlaceholderRef) {
      this.componentPlaceholderRef.clear();
    }
    switch (tab) {
      case "Teams":
        this.loadLocationTeams();
        break;
      case "Activity Log":
        this.loadActivityLog();
        break;
      case "Facilities":
        this.loadFacilitiesView();
        break;
      case "Payers":
        this.loadPayersView();
        break;
      default:
        break;
    }
  }

  loadLocationTeams() {
    import("../location-group/location-group.module").then((modules) => {
      const lazyModule = modules["LocationGroupModule"];
      const lazyComponent = lazyModule.getLocationTeamsComponent();
      const factory = this.resolver.resolveComponentFactory(lazyComponent);
      const componentRef =
        this.componentPlaceholderRef.createComponent(factory);
    });
  }

  loadActivityLog() {
    import("components/components.module").then((modules) => {
      const lazyModule = modules["ComponentsModule"];
      const lazyComponent = lazyModule.getActivityLogComponent();
      const factory = this.resolver.resolveComponentFactory(lazyComponent);
      const componentRef =
        this.componentPlaceholderRef.createComponent(factory);
      // (componentRef.instance as any).companyData = this.companyData;
      // (componentRef.instance as any).companyLocations = this.location;
    });
  }

  loadFacilitiesView() {
    import("../facilities-view/facilities-view.module").then((modules) => {
      const lazyModule = modules["FacilitiesViewPageModule"];
      const lazyComponent = lazyModule.getFacilitiesViewComponent();
      const factory = this.resolver.resolveComponentFactory(lazyComponent);
      const componentRef =
        this.componentPlaceholderRef.createComponent(factory);
    });
  }

  loadPayersView() {
    this.loadPayersViewComponent();
    this.runReport();
  }

  loadPayersViewComponent() {
    import("../payers-view/payers-view.module").then((modules) => {
      const lazyModule = modules["PayersViewPageModule"];
      const lazyComponent = lazyModule.getPayersViewComponent();
      const factory = this.resolver.resolveComponentFactory(lazyComponent);
      const componentRef =
        this.componentPlaceholderRef.createComponent(factory);
    });
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this.subscriptions.forEach((val) => val.unsubscribe());
    if (this.payerReportSubscribed) {
      // Pass the handler: Events.unsubscribe(topic) alone drops the topic for
      // every other subscriber in the app, not just ours.
      this.events.unsubscribe("runPayerReport", this.payerReportHandler);
      this.payerReportSubscribed = false;
    }
  }

  runReport() {
    // loadPayersView() runs on every Payers-tab selection, so subscribing
    // unconditionally would stack handlers and fire N requests per button press.
    if (this.payerReportSubscribed) {
      return;
    }
    this.payerReportSubscribed = true;
    this.events.subscribe("runPayerReport", this.payerReportHandler);
  }

  private onRunPayerReport() {
    const user = this.authSvc.getUser();
    const tenantId = user ? user.tenantId : null;
    this.reportTitle = this.translate.instant("payerReport");
    const reportName = "payer";
    this.buildQSPayloadAndGetDashboardURL(reportName, tenantId);
  }

  async buildQSPayloadAndGetDashboardURL(
    reportName: string,
    tenantId: number
  ): Promise<void> {
    try {
      const user = this.authSvc.getUser() || ({} as any);
      let payload = {
        dashboard_name: reportName,
        username: tenantId ? tenantId.toString() : "",
        id_token: await this.authSvc.getValidIdToken(),
        email: user.email,
        parameters: {
          tenant_id: tenantId ? tenantId.toString() : "",
          display_flag: "All",
        },
      };
      this.getQSDashboard(payload);
    } catch (err) {
      // An unhandled rejection here used to fail silently: no report, no error.
      console.error("Error: QuickSight payload. ", err);
      this.reportService.presentErrorToast(
        err,
        "Failed to retrieve QuickSight Dashboard."
      );
    }
  }

  getQSDashboard(payload, retryCount = 0) {
    const maxRetries = 1;
    this.reportService.increaseSpinnerCount();
    this.reportService
      .getQuicksightDashboardAPIForRegisteredUser(payload)
      .subscribe(
        (response) => {
          if (response) {
            this.showReport = true;
            this.qsDashboard = this.sanitizer.bypassSecurityTrustResourceUrl(
              response.toString()
            );
          }
          this.reportService.decreaseSpinnerCount();
        },
        (err) => {
          console.error("Error: QuickSight Dashboard. ", err);
          // Balance THIS attempt's spinner before deciding what to do next.
          // Retrying without doing so leaked one count per retry and left the
          // blocking overlay up forever.
          this.reportService.decreaseSpinnerCount();
          if (
            err &&
            err.message &&
            err.message.includes(
              "operation error Cognito Identity: GetId, https response error StatusCode: 400"
            ) &&
            retryCount < maxRetries
          ) {
            this.getQSDashboard(payload, retryCount + 1);
          } else {
            this.reportService.presentErrorToast(
              err,
              "Failed to retrieve QuickSight Dashboard."
            );
            this.resetQSDashboard();
          }
        }
      );
  }

  resetQSDashboard() {
    this.qsDashboard = null;
    this.showReport = false;
  }

  backToReportsMenu() {
    this.showReport = false;
    // showReport gates an *ngIf around the whole tab area, so leaving the report
    // destroys and rebuilds #componentPlaceholder. Without re-dispatching, the
    // user lands on an empty panel with the tab still highlighted.
    this.reloadActiveTab();
  }

  private reloadActiveTab() {
    const tab = this.activeTab;
    if (!tab) {
      return;
    }
    this.loadedTab = null;
    this.activeTab = null;
    this.setActiveTab(tab);
  }
}



// WEBPACK FOOTER //
// ./src/pages/company/location-group/location-group.ts